/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    object, shape, string, number, arrayOf, func, bool,
} from 'prop-types';
import { Fab } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ReactHTMLParser from 'react-html-parser';
import { withStyles } from '@material-ui/core/styles';
import { secureProtocol } from '../../../utils/secureProtocol';
import SubscriptionStatusChips from './SubscriptionStatusChips';
import ResponsiveImage from '../../../../../components/GraphqlComponents/GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';

const styles = (theme) => ({
    root: {
        width: '44px',
        height: '44px',
        background: theme.palette?.cms?.activeButtonColor || '#E8F1ED',
        '@media screen and (max-width: 400px)': {
            width: '36px',
            height: 0,
        },
    },
    container: {
        display: 'flex',
        position: 'relative',
    },
    canceledStatusCont: {
        paddingBottom: '11px',
        '@media screen and (min-width: 768px)': {
            paddingBottom: 0,
        },
    },
    subscriptionTitle: {
        marginBottom: '17px',
        verticalAlign: 'top',
    },
    subscriptionDetailsContainer: {
        fontWeight: '600',
        width: '100%',
        fontSize: '15px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '@media screen and (min-width: 768px)': {
            fontSize: '16px',
        },
    },
    subscriptionDetails: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '@media screen and (min-width: 1024px)': {
            height: 'auto',
            justifyContent: 'inherit',
        },
        '@media screen and (max-width: 400px)': {
            fontSize: '12px',
        },
    },
    pausedStatus: {
        color: '#595959',
    },
    activeStatus: {
        color: '#008000',
    },
    canceledStatus: {
        color: '#a1001a',
    },
    buttonContainer: {
        position: 'absolute',
        bottom: 0,
        right: 0,
    },
    subProdImage: {
        maxWidth: '225px',
        width: 'calc(100% - 100px)',
        marginRight: '20px',
        '& img': {
            width: '100%',
            aspectRatio: '117/128',
        },
    },
    canceledSubProdImage: {
        opacity: 0.7,
    },
    subscriptionPriceContainer: {
        '@media screen and (min-width: 768px)': {
            fontWeight: 400,
        },
        '@media screen and (min-width: 1024px)': {
            marginBottom: '15px',
        },
    },
    subStatusChips: {
        display: 'flex',
        justifyContent: 'space-between',
        '@media screen and (max-width: 1023px)': {
            display: 'none',
        },
    },
    payUpFrontMessaging: {
        color: 'red',
        width: '80%',
        alignSelf: 'center',
        '@media screen and (min-width: 1440px)': {
            width: '100%',
        },
    },
});

function SubscriptionSummary({
    subscription, classes, mode, handleExpand, handleMinimize, currentSubscription,
    handleCancelModal, handleSkipModal, handlePauseModal, handleResumeModal, pastEndDate,
}) {
    let subscriptionStatus;
    let subscriptionStatusClass;
    const updateStatusForExpiredProduct = () => {
        if (pastEndDate) {
            subscriptionStatus = 'COMPLETED';
            subscriptionStatusClass = classes.activeStatus;
        }
    };

    switch (subscription.subscriptionStatus) {
        case 'PROCESSED':
            subscriptionStatus = 'ACTIVE';
            subscriptionStatusClass = classes.activeStatus;
            updateStatusForExpiredProduct();
            break;
        case 'UPDATED':
            subscriptionStatus = 'UPDATED';
            subscriptionStatusClass = classes.activeStatus;
            updateStatusForExpiredProduct();
            break;
        case 'CANCELLED':
            subscriptionStatus = 'CANCELLED';
            subscriptionStatusClass = classes.canceledStatus;
            break;
        case 'SKIPPED':
            subscriptionStatus = 'SKIPPED';
            subscriptionStatusClass = classes.pausedStatus;
            updateStatusForExpiredProduct();
            break;
        case 'RESUMED':
            subscriptionStatus = 'RESUMED';
            subscriptionStatusClass = classes.pausedStatus;
            updateStatusForExpiredProduct();
            break;
        default:
            subscriptionStatus = 'PAUSED';
            subscriptionStatusClass = classes.pausedStatus;
            updateStatusForExpiredProduct();
    }

    const fallbackImgURL = 'https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltfc354cd0e7f66f1b/6267900f6b305b67539338d2/image_not_available.png';
    const productImage = subscription?.productImageCategory && subscription?.productImageCategory !== '' ? secureProtocol(subscription?.productImageCategory) : fallbackImgURL;
    const isPayupFrontClub = (selectedSubscription) => selectedSubscription?.subscriptionType?.toLowerCase() === 'monthonlypayfull';

    return (
        <div className={`${subscription.subscriptionStatus === 'CANCELLED' && classes.canceledStatusCont} ${classes.container}`}>
            <div className={`${subscription.subscriptionStatus === 'CANCELLED' && classes.canceledSubProdImage} ${classes.subProdImage}`}> <ResponsiveImage path={productImage} alt={subscription.productName}  params={{ desktop: 'quality=100', tablet: 'quality=100', mobile: 'quality=100' }} /></div>
            <div className={classes.subscriptionDetailsContainer}>
                <div  className={classes.subscriptionDetails}>
                    <div>
                        <div className={classes.subscriptionTitle}>
                            {ReactHTMLParser(subscription.productName)}
                        </div>
                        <div className={classes.subscriptionPriceContainer}>
                            <div>
                                <span className={classes.subLabel}>Item# </span>
                                <span className={classes.subLabel}>{subscription.productId}</span>
                            </div>
                            <div>
                                <span className={classes.subLabel}>Price: </span>
                                <span className={classes.subLabel}>${subscription.price}</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <span className={classes.subLabel}>Status: </span>
                        <span className={subscriptionStatusClass}>{subscriptionStatus}</span>
                    </div>
                </div>
                <div className={classes.subStatusChips}>
                    { subscription.subscriptionStatus !== 'CANCELLED' && !pastEndDate && (
                        <>
                            { !isPayupFrontClub(subscription) ? (
                                <SubscriptionStatusChips
                                    subscription={subscription}
                                    handleCancelModal={handleCancelModal}
                                    handleSkipModal={handleSkipModal}
                                    handlePauseModal={handlePauseModal}
                                    handleResumeModal={handleResumeModal}
                                />
                            ) : <div className={classes.payUpFrontMessaging}>Please contact customer service if you would like to cancel or update this subscription product</div>}
                            { mode === 'expand' && currentSubscription === subscription.ruleId
                                ? <Fab className={classes.root} onClick={() => handleMinimize()}><RemoveIcon /></Fab>
                                : <Fab className={classes.root} onClick={() => handleExpand(subscription.ruleId)}><AddIcon /></Fab>}
                        </>
                    )}
                </div>
            </div>
            { (subscription.subscriptionStatus === 'CANCELLED' || pastEndDate) &&  (
                <div className={classes.buttonContainer}>
                    { mode === 'expand' && currentSubscription === subscription.ruleId
                        ? <Fab className={classes.root} onClick={() => handleMinimize()}><RemoveIcon /></Fab>
                        : <Fab className={classes.root} onClick={() => handleExpand(subscription.ruleId)}><AddIcon /></Fab>}
                </div>
            )}
        </div>
    );
}

SubscriptionSummary.defaultProps = {
    mode: '',
    currentSubscription: '',
};

SubscriptionSummary.propTypes = {
    subscription: shape({
        checkoutBrand: string,
        duration: number.isRequired,
        endDate: string.isRequired,
        interval: number.isRequired,
        nextOrderDate: string,
        orderDate: string.isRequired,
        orderHistoryResult: arrayOf(
            shape({
                deliveryDate: string,
                discountAmount: string,
                orderDate: string.isRequired,
                orderNumber: string.isRequired,
                productCode: string.isRequired,
                productName: string.isRequired,
                productPrice: string.isRequired,
                quantity: string.isRequired,
            }),
        ),
        orderNumber: string.isRequired,
        orderTime: string.isRequired,
        orderType: string.isRequired,
        price: string.isRequired,
        productId: string.isRequired,
        productImageCategory: string.isRequired,
        productImageThumbnail: string.isRequired,
        productName: string.isRequired,
        quantity: string,
        recipient: arrayOf(shape({
            city: string.isRequired,
            country: string.isRequired,
            firstName: string.isRequired,
            lastName: string.isRequired,
            state: string.isRequired,
            street1: string.isRequired,
            street2: string.isRequired,
            zipcode: string.isRequired,
        })),
        ruleId: string.isRequired,
        startDate: string.isRequired,
        subscriptionStatus: string.isRequired,
        totalAmount: string.isRequired,
    }).isRequired,
    classes: object.isRequired,
    handleMinimize: func.isRequired,
    handleExpand: func.isRequired,
    handleCancelModal: func.isRequired,
    handleSkipModal: func.isRequired,
    handlePauseModal: func.isRequired,
    handleResumeModal: func.isRequired,
    mode: string,
    currentSubscription: string,
    pastEndDate: bool.isRequired,
};

export default withStyles(styles, { withTheme: true })(SubscriptionSummary);
